import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

interface SettingsSectionHeaderProps {
  title: string;
  description?: string;
  /** Right-aligned controls on the title row (search, add button, …). */
  actions?: ReactNode;
}

/**
 * Compact, shared header for a Settings sub-section: a "Back to Settings" link
 * above a single row of `title (+ description) … actions`. Keeps every section
 * visually consistent and vertically tight.
 */
export function SettingsSectionHeader({ title, description, actions }: SettingsSectionHeaderProps) {
  return (
    <div className="space-y-3">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Settings
      </Link>

      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
          {description && <p className="truncate text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="ml-auto flex shrink-0 items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
}
